export interface ImpersonationSession {
  id: string;
  adminUserId: string;
  adminEmail: string;
  targetUserId: string;
  targetEmail: string;
  reason: string;
  startedAt: string;
  expiresAt: string;
  endedAt?: string;
  auditRef?: string;
}

const STORAGE_KEY = 'kontora_impersonations_v1';
const MAX_DURATION_MINUTES = 60;

export function loadImpersonations(): ImpersonationSession[] {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as ImpersonationSession[];
  } catch {
    return [];
  }
}

export function saveImpersonation(session: ImpersonationSession): ImpersonationSession {
  const list = loadImpersonations();
  const idx = list.findIndex((s) => s.id === session.id);
  if (idx >= 0) list[idx] = session;
  else list.unshift(session);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {}
  return session;
}

export function startImpersonation(opts: {
  adminUserId: string;
  adminEmail: string;
  targetUserId: string;
  targetEmail: string;
  reason: string;
}): ImpersonationSession {
  const now = new Date();
  const expires = new Date(now.getTime() + MAX_DURATION_MINUTES * 60 * 1000);
  const session: ImpersonationSession = {
    id: createImpersonationId(),
    adminUserId: opts.adminUserId,
    adminEmail: opts.adminEmail,
    targetUserId: opts.targetUserId,
    targetEmail: opts.targetEmail,
    reason: opts.reason,
    startedAt: now.toISOString(),
    expiresAt: expires.toISOString(),
  };
  return saveImpersonation(session);
}

export function endImpersonation(id: string): ImpersonationSession | undefined {
  const session = loadImpersonations().find((s) => s.id === id);
  if (!session) return undefined;
  const ended = { ...session, endedAt: new Date().toISOString() };
  saveImpersonation(ended);
  return ended;
}

export function getActiveImpersonation(): ImpersonationSession | null {
  const active = loadImpersonations().find(
    (s) => !s.endedAt && new Date(s.expiresAt).getTime() > Date.now(),
  );
  return active ?? null;
}

export function createImpersonationId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `imp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function isImpersonationExpired(session: ImpersonationSession): boolean {
  return new Date(session.expiresAt).getTime() < Date.now();
}

export function formatImpersonationDuration(session: ImpersonationSession): string {
  const start = new Date(session.startedAt).getTime();
  const now = Date.now();
  const seconds = Math.floor((now - start) / 1000);
  const remaining = Math.max(0, Math.floor((new Date(session.expiresAt).getTime() - now) / 1000));
  return `Aktiv seit ${Math.floor(seconds / 60)} min · Verbleibend ${Math.floor(remaining / 60)} min`;
}
